import {
  Box,
  Droplets,
  Flame,
  Layers,
  Ruler,
  SprayCan,
  TrainFront,
  Workflow,
  Wrench,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';

import { cn } from '~/lib/cn';

/**
 * Supported icon names used throughout the ARPA website content.
 * Maps semantic names (from content JSON) to Lucide components.
 * Colour is controlled by `color` CSS / `currentColor` — set text colour on a parent.
 */
export type IconName =
  | 'railway' // TrainFront — rolling-stock contexts
  | 'flame' // Flame — fire-retardant / thermal properties
  | 'process' // Workflow — manufacturing process
  | 'infusion' // Droplets — infusion technology
  | 'cube' // Box — 3D / product form
  | 'ruler' // Ruler — precision / engineering
  | 'layers' // Layers — composite layup
  | 'wrench' // Wrench — assembly / maintenance
  | 'spray' // SprayCan — painting / coating
  | 'train'; // TrainFront — alias for railway icon

export const iconNames: IconName[] = [
  'railway',
  'flame',
  'process',
  'infusion',
  'cube',
  'ruler',
  'layers',
  'wrench',
  'spray',
  'train',
];

const iconMap: Record<IconName, React.ComponentType<LucideProps>> = {
  railway: TrainFront,
  flame: Flame,
  process: Workflow,
  infusion: Droplets,
  cube: Box,
  ruler: Ruler,
  layers: Layers,
  wrench: Wrench,
  spray: SprayCan,
  train: TrainFront,
};

export interface IconProps {
  name: IconName;
  /** Icon size in px — mapped to lucide's `size` prop. Default: 32 */
  size?: number;
  className?: string;
}

/**
 * Resolves a semantic icon name to a Lucide icon component.
 * Uses `absoluteStrokeWidth` to keep stroke at 1.5px regardless of size.
 * Colour is inherited from `currentColor` — control via parent's text colour.
 */
function Icon({ name, size = 32, className }: IconProps) {
  const LucideIcon = iconMap[name];

  return (
    <LucideIcon
      size={size}
      strokeWidth={1.5}
      absoluteStrokeWidth
      aria-hidden="true"
      className={cn(className)}
    />
  );
}

export { Icon };
export default Icon;
